/**
 * Pre-flash audit backup — mirrors WinKFP's actual backup behaviour
 * (faithful, no embellishment).
 *
 * **What WinKFP actually does** (verified across 4 functions in
 * winkfpt.exe on 2026-05-27, see docs/architecture.md §11.6):
 *
 *   - `FUN_00432300` — dispatches `ZIF_BACKUP` IPO job, reads
 *     ZIF_BACKUP_SG_KENNUNG/PROJEKT/PROGRAMM_STAND/BMW_HW/BMW_PST/STATUS
 *     cabd-pars, appends to `REF.OUT` as audit log.
 *   - `coapiKfCheckReferenzD2` (FUN_00450300) — uses ZIF_BACKUP as an
 *     IDENTITY-FALLBACK when current ZIF reads garbage.
 *   - `coapiKfGetHwNrFromSgD2` (FUN_00445900) — uses ZIF_BACKUP as
 *     HW-NR FALLBACK when SG_IDENT_LESEN gives garbage.
 *   - `WinKFP.INI` defines `KomfortKonfPath=C:\\NFS-Backup` — that's
 *     where coding/comfort BACKUPS go, NOT firmware.
 *
 * **What WinKFP does NOT do: there is no firmware backup feature.**
 * `ZIF_BACKUP` is on-ECU REDUNDANCY (the ECU keeps a backup copy of
 * its identity in a separate flash region — used to recover from a
 * failed flash mid-write). It's NOT a host-side ROM save. BMW's
 * architecture relies on SP-Daten as the canonical firmware source;
 * if you brick, you re-flash from SP-Daten.
 *
 * **What this `runBackup` does:** captures the same IPO-driven
 * identity + audit state WinKFP records, persists as JSON. Useful
 * as a pre-flash snapshot for forensic / audit purposes. NOT a
 * brick-recovery image. Adding raw flash dumps via SGBD-specific
 * jobs would violate the IPO-driven principle that scopes our
 * design (see [[feedback-precheck-ipo-driven]]).
 */
/** Default jobs every backup runs — the universal IPO-level reads. */
export const DEFAULT_BACKUP_JOBS = [
    'HW_REFERENZ',
    'SG_STATUS_LESEN',
    'SG_IDENT_LESEN',
    'SG_AIF_LESEN',
    // ZIF_BACKUP reads the ECU's redundant identity region. WinKFP's
    // FUN_00432300 dispatches this last; we mirror the order. The IPO
    // publishes ZIF_BACKUP_SG_KENNUNG/PROJEKT/PROGRAMM_STAND/BMW_HW/
    // BMW_PST/STATUS as result cabd-pars. WinKFP treats status code
    // 0x427 as "no backup data" (not an error) — `runBackup` records
    // it in the report rather than aborting.
    'ZIF_BACKUP',
];
/**
 * WinKFP-specific success code for `ZIF_BACKUP` IPO when the ECU
 * doesn't have a populated backup region. Observed in
 * `FUN_00432300` as a special-case branch that closes the audit
 * file and returns success.
 */
export const ZIF_BACKUP_NOT_AVAILABLE = 0x427;
/**
 * Read the target SG via its IPO and capture every cabd-par each
 * dispatch publishes. Returns a `BackupReport` ready to be JSON-
 * serialised + persisted with `writeBackupFile`.
 *
 * Same cabimain dispatch pattern as `runPrecheck` — one runtime
 * handle, multiple jobs run in sequence, cabd-pars accumulate. The
 * difference is intent: precheck gates a flash, backup persists
 * state.
 */
export async function runBackup(ecu, ediabas, startRuntime, opts = {}) {
    const jobs = opts.jobs ?? DEFAULT_BACKUP_JOBS;
    const handle = await startRuntime(ecu.ipoPath, {
        sgbd: ecu.sgbd,
        ediabas,
        cabdPars: { HWNR_IS_NEW: '0', ...(opts.cabdPars ?? {}) },
        workingDir: ecu.workingDir,
    });
    const ipoDispatches = {};
    for (const job of jobs) {
        try {
            await handle.runCabimain(job);
            ipoDispatches[job] = {
                setjobstatus: handle.state.lastJobStatus,
                cabdPars: Object.fromEntries(handle.state.cabdPars),
            };
        }
        catch (err) {
            ipoDispatches[job] = {
                error: err instanceof Error ? err.message : String(err),
            };
        }
    }
    // Merge per-dispatch cabd-pars with "last non-empty wins". Some
    // IPO jobs (notably ZIF_BACKUP) clear cabd-par keys their callers
    // populated earlier — e.g. ZIF_BACKUP wipes AIF_* by calling
    // CDHSetCabdPar with empty strings as part of its normal flow.
    // Without this merge, the post-dispatch handle.state would lose
    // the AIF values SG_AIF_LESEN published moments earlier.
    // Use ordered iteration so a later non-empty value overrides; an
    // earlier non-empty value survives a later empty-string write.
    const merged = {};
    for (const result of Object.values(ipoDispatches)) {
        if (!('cabdPars' in result))
            continue;
        for (const [k, v] of Object.entries(result.cabdPars)) {
            if (v !== '')
                merged[k] = v;
            else if (!(k in merged))
                merged[k] = '';
        }
    }
    return {
        timestamp: new Date().toISOString(),
        schemaVersion: 1,
        target: {
            sgbd: ecu.sgbd,
            ipoPath: ecu.ipoPath,
            diagAddr: ecu.diagAddr,
            expectedHwnr: ecu.expectedHwnr,
        },
        ipoDispatches,
        finalCabdPars: merged,
        systemData: Object.fromEntries(handle.state.systemData),
    };
}
/**
 * Serialize a `BackupReport` to the canonical JSON bytes that
 * `writeBackupFile` (Node) and the browser's Blob-download emitter
 * both wrap. Trailing newline preserved for git-friendly diffs.
 */
export function serializeBackup(report) {
    return new TextEncoder().encode(JSON.stringify(report, null, 2) + '\n');
}
/**
 * Route a `BackupReport` through a `BackupEmitter`. Resolves to the
 * caller-visible location the emitter returned (path / download name)
 * or `undefined` when the emitter doesn't report one.
 */
export async function emitBackup(report, emitter, filename) {
    const name = filename ?? defaultBackupFilename(report);
    const bytes = serializeBackup(report);
    return (await emitter.emit(name, bytes)) ?? undefined;
}
/**
 * Build the canonical backup filename — used when the caller doesn't
 * specify one. Format: `<HWNR>-<ZB>-<sanitised-timestamp>.json`.
 * Falls back to `unknown` for any field we couldn't recover.
 */
export function defaultBackupFilename(report) {
    const hwnr = report.finalCabdPars['ID_BMW_NR'] ?? report.target.expectedHwnr ?? 'unknownHwnr';
    const zb = report.finalCabdPars['AIF_ZB_NR'] ?? 'unknownZb';
    // ISO-8601 has `:` chars that are illegal on FAT/Win; replace + strip ms.
    const ts = report.timestamp.replace(/:/g, '-').replace(/\.\d{3}Z$/, 'Z');
    return `${hwnr}-${zb}-${ts}.json`;
}
//# sourceMappingURL=backup.js.map